import type { SelectionDiff, ServiceTreeNode } from "../types/ServiceTreeTypes";
import type { AvailableServiceGroup } from "@repo/server-api";

/**
 * Hierarchische API-Response zu Tree Nodes transformieren
 */
export function buildServiceTree(serviceGroups: AvailableServiceGroup[]): ServiceTreeNode[] {
	return serviceGroups.map((group) => ({
		id: group.id,
		label: group.name,
		type: "group" as const,
		data: group,
		children: group.services.map((service) => ({
			id: service.id,
			label: service.name,
			type: "service" as const,
			data: service,
			parentId: group.id,
		})),
	}));
}

/**
 * Alle Service-IDs aus Tree extrahieren (ohne Gruppen)
 */
export function getAllServiceIds(nodes: ServiceTreeNode[]): string[] {
	const ids: string[] = [];
	nodes.forEach((node) => {
		if (node.type === "service") {
			ids.push(node.id);
		}
		if (node.children) {
			ids.push(...getAllServiceIds(node.children));
		}
	});
	return ids;
}

/**
 * Service-IDs innerhalb einer Gruppe
 */
export function getServiceIdsInGroup(groupId: string, nodes: ServiceTreeNode[]): string[] {
	const group = nodes.find((n) => n.id === groupId);
	if (!group || !group.children) return [];
	return group.children.map((child) => child.id);
}

/**
 * Diff zwischen initialer und aktueller Selection berechnen
 */
export function computeSelectionDiff(
	currentSelection: Set<string>,
	initialServiceIds: Set<string>,
	treeNodes: ServiceTreeNode[],
): SelectionDiff {
	const allServiceIds = getAllServiceIds(treeNodes);

	// Nur Service-IDs berücksichtigen (keine Gruppen)
	const currentServiceIds = Array.from(currentSelection).filter((id) => allServiceIds.includes(id));

	const addedServiceIds = currentServiceIds.filter((id) => !initialServiceIds.has(id));

	const removedServiceIds = Array.from(initialServiceIds).filter(
		(id) => !currentServiceIds.includes(id),
	);

	return { addedServiceIds, removedServiceIds };
}

/**
 * Parent-Child Selection synchronisieren
 */
export function syncParentChildSelection(
	selection: Set<string>,
	treeNodes: ServiceTreeNode[],
): Set<string> {
	const result = new Set(selection);

	// Für jede Gruppe: Wenn alle Kinder selected, dann Gruppe auch selected
	treeNodes.forEach((node) => {
		if (node.type === "group" && node.children) {
			const allChildrenSelected = node.children.every((child) => result.has(child.id));
			if (allChildrenSelected && node.children.length > 0) {
				result.add(node.id);
			} else {
				result.delete(node.id);
			}
		}
	});

	return result;
}
